import { navigateTo, useAsyncData, useNuxtApp, useRoute } from '#imports'

import type {
  MultipleQueriesQuery,
  SearchOptions,
} from '@algolia/client-search'
import type {
  ModelComparisonCompareModels,
  ModelComparisonModel,
} from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { isBrowser } from '@backmarket/utils/env/isBrowser'
import { isEqual } from '@backmarket/utils/object/isEqual'
import algoliasearch from 'algoliasearch'

import { isNotNullNorUndefined } from '~/utils/object/isNotNullNorUndefined'

import type { SelectedModels } from '../models/selected-models'
import { mapAlgoliaResponsesToModelCharacteristics } from '../utils/algolia/algoliaAdapter'
import type {
  ModelsComparisonAlgoliaHit,
  ModelsComparisonAlgoliaResponse,
} from '../utils/algolia/comparable-model-algolia-hit'
import translations from '../utils/characteristics.translations'

function shouldRedirect(
  link: ModelComparisonCompareModels['link'],
  currentRoute: ReturnType<typeof useRoute>,
) {
  // no client-side redirection to avoid duplicate requests and page_viewed event
  // a client-side redirection triggers a second Vue Router afterEach hook, causing the duplicate event
  // see issue reported here https://github.com/nuxt/nuxt/issues/14287
  if (isBrowser()) return false

  const isCanonicalUrl = isEqual(link.params, currentRoute.params)

  return !isCanonicalUrl
}

function replaceModelNameInFilters<ExtraParams extends object>({
  params,
  model,
}: {
  model: { catalogName: string; compareName: string }
  params: {
    facetFilters: (string | string[])[]
  } & ExtraParams
}) {
  const fixedFacetFilters = params.facetFilters.map((filter) =>
    Array.isArray(filter)
      ? filter
      : filter
          .replace('CATALOG_NAME', model.catalogName)
          .replace('COMPARE_NAME', model.compareName),
  )

  return {
    ...params,
    facetFilters: fixedFacetFilters,
  }
}

function replaceModelNameInFiltersTransformer(model: ModelComparisonModel) {
  return function transformer(queries: MultipleQueriesQuery[]) {
    return queries.map((q) => {
      const paramsWithModelName = replaceModelNameInFilters({
        model,
        params: q.params as SearchOptions & {
          facetFilters: (string | string[])[]
        },
      })

      return {
        indexName: q.indexName,
        params: paramsWithModelName,
      }
    })
  }
}

function useRedirectToCanonicalLinkIfNeeded() {
  const currentRoute = useRoute()
  const nuxtApp = useNuxtApp()

  async function redirectToCanonicalLinkIfNeeded(
    link: ModelComparisonCompareModels['link'],
  ) {
    if (!shouldRedirect(link, currentRoute)) return

    const newRoute = {
      ...currentRoute,
      params: link.params,
    }

    /** navigateTo is a composable that can only be run in a context where it has access to the Nuxt instance
     * see @https://nuxt.com/docs/api/composables/use-nuxt-app#runwithcontext
     * and navigateTo source code @https://github.com/nuxt/nuxt/blob/fd7d9d26e260f0b16ad7848e5f5425548854da62/packages/nuxt/src/app/composables/router.ts#L112
     */
    await nuxtApp.runWithContext(() =>
      navigateTo(newRoute, { replace: true, redirectCode: 301 }),
    )
  }

  return { redirectToCanonicalLinkIfNeeded }
}

function useGetModelsSlugsFromRoute() {
  const route = useRoute()
  const { productA, productB } = route.params as {
    productA: string
    productB: string
  }
  const modelsSlugs = [productA, productB]

  return { modelsSlugs }
}

function useBuildModelsComparisonData() {
  const i18n = useI18n()
  const locale = useI18nLocale()

  function buildModelsComparisonData({
    comparisonResults,
    modelsData,
  }: {
    comparisonResults: ModelComparisonCompareModels
    modelsData: {
      algoliaData: ModelsComparisonAlgoliaResponse[]
      model: ModelComparisonModel
    }[]
  }) {
    const modelsNames = comparisonResults.models.map(
      (model) => model.compareName,
    ) as SelectedModels

    const characteristicsOfEachModel = modelsData
      ? modelsData.filter(isNotNullNorUndefined).map(({ algoliaData }) => {
          return mapAlgoliaResponsesToModelCharacteristics(algoliaData, {
            i18n,
            translations,
            locale,
          })
        })
      : []

    return {
      category: comparisonResults.category,
      content: comparisonResults.content,
      algoliaConfig: comparisonResults.algoliaConfig,
      link: comparisonResults.link,
      comparison: {
        modelsNames,
        characteristics: characteristicsOfEachModel,
      },
    }
  }

  return { buildModelsComparisonData }
}

export function useFetchModelsComparison() {
  const { modelsSlugs } = useGetModelsSlugsFromRoute()
  const { redirectToCanonicalLinkIfNeeded } =
    useRedirectToCanonicalLinkIfNeeded()

  const compareOptions = useAlgoliaSearchOptions('compare')
  const searchOptions = useAlgoliaSearchOptions('search')
  const { buildModelsComparisonData } = useBuildModelsComparisonData()
  const $httpFetch = useHttpFetch()

  return useAsyncData(
    getAsyncDataKey('models-comparison', {
      slug1: modelsSlugs[0],
      slug2: modelsSlugs[1],
    }),
    async () => {
      const comparisonResults = await $httpFetch(
        '/bm/models-comparison/compare/:model1/:model2',
        {
          pathParams: {
            model1: modelsSlugs[0],
            model2: modelsSlugs[1],
          },
        },
      )

      await redirectToCanonicalLinkIfNeeded(comparisonResults.link)

      const compareAlgoliaClient = algoliasearch(
        'dummy_app_id',
        'dummy_key',
        compareOptions,
      )

      const searchAlgoliaClient = algoliasearch(
        'dummy_app_id',
        'dummy_key',
        searchOptions,
      )

      const algoliaPromises: Promise<ModelsComparisonAlgoliaResponse>[] = []

      const comparisons = comparisonResults.algoliaConfig.comparison || []

      comparisonResults.models.forEach((model) => {
        const includeModelInFilter = replaceModelNameInFiltersTransformer(model)

        comparisons.forEach(({ queries }) => {
          const transformedQueries = includeModelInFilter(queries)

          const algoliaPromise = queries[0]?.indexName.startsWith('acq_')
            ? (compareAlgoliaClient.search<ModelsComparisonAlgoliaHit>(
                transformedQueries,
              ) as Promise<ModelsComparisonAlgoliaResponse>)
            : (searchAlgoliaClient.search<ModelsComparisonAlgoliaHit>(
                transformedQueries,
              ) as Promise<ModelsComparisonAlgoliaResponse>)

          algoliaPromises.push(algoliaPromise)
        })
      })

      const data = await Promise.all(algoliaPromises)

      const modelsData = [
        { algoliaData: [data[0], data[1]], model: comparisonResults.models[0] },
        { algoliaData: [data[2], data[3]], model: comparisonResults.models[1] },
      ]

      const comparison = buildModelsComparisonData({
        modelsData,
        comparisonResults,
      })

      return comparison
    },
  )
}
